// MaturaQuadraticRevenueClosedTask.js
// Zadanie zamknięte – optymalizacja zysku: Z(x) = (A + Bx)(C - x)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import { shuffle } from "../../../../utils/shuffle";

const randInt = (a, b) =>
  Math.floor(Math.random() * (b - a + 1)) + a;

function generateJune2025Original() {
  return {
    type: "MaturaQuadraticRevenueClosedTask",
    data: {
      A: 500,
      B: 50,
      C: 16,
      maxX: 14,
      xOpt: 3,
      options: [
        { label: "A", latex: "3", value: 3, isCorrect: true },
        { label: "B", latex: "4", value: 4, isCorrect: false },
        { label: "C", latex: "7", value: 7, isCorrect: false },
        { label: "D", latex: "14", value: 14, isCorrect: false },
      ],
      correctIndex: 0,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 31,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return generateJune2025Original();
  }

  // Wzorujemy się na Z(x) = (500 + 50x)(16 - x)
  // Ogólnie: Z(x) = (B * r + Bx)(C - x)
  // a = -B, b = B*C - B*r, x_opt = (C - r)/2

  let B, r, C, xOpt, maxX;

  while (true) {
    B = [20, 25, 50][randInt(0, 2)]; // „nachylenie” ilości sztuk
    r = randInt(6, 14);              // P0 / B
    C = randInt(r + 2, r + 10);      // liczba typu „16”

    if ((C - r) % 2 !== 0) continue;

    xOpt = (C - r) / 2;
    if (xOpt < 1) continue;

    // Chcemy mieć co najmniej 4 możliwe wartości x (1..maxX),
    // żeby było co najmniej 3 różne błędne odpowiedzi.
    const minMaxX = Math.max(xOpt + 3, 4);
    const maxMaxX = C - 1;

    if (maxMaxX < minMaxX) continue;

    maxX = randInt(minMaxX, maxMaxX);
    break;
  }

  const P0 = B * r;
  const A = P0; // żeby było wprost jak w CKE

  const correct = xOpt;

  // Wszystkie możliwe błędne odpowiedzi w przedziale 1..maxX
  const allWrong = [];
  for (let v = 1; v <= maxX; v += 1) {
    if (v !== correct) allWrong.push(v);
  }

  // Tasujemy i bierzemy trzy pierwsze jako dystraktory
  const wrongValues = shuffle(allWrong).slice(0, 3);
  const allValues = shuffle([correct, ...wrongValues]);

  const options = allValues.map((v, idx) => ({
    value: v,
    latex: String(v),
    isCorrect: v === correct,
    label: String.fromCharCode("A".charCodeAt(0) + idx),
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaQuadraticRevenueClosedTask",
    data: {
      A,
      B,
      C,
      maxX,
      xOpt,
      options,
      correctIndex,
    },
  };
}

// ============ TREŚĆ ============

export function renderTask({ data }) {
  const { A, B, C, maxX, options } = data;

  const ZxLatex = `Z(x) = ( ${A} + ${B}x )( ${C} - x )`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        <strong>Zadanie maturalne – zamknięte (0–1 pkt).</strong>
      </p>

      <p>
        Producent latarek przeanalizował wpływ zmiany ceny latarki <InlineMath math="\mathrm{L25}" />
        na liczbę kupujących ten produkt. Z analizy wynika, że roczny
        zysk <InlineMath math={"Z"} /> ze sprzedaży latarek <InlineMath math="\mathrm{L25}" />
        wyraża się wzorem
      </p>

      <BlockMath math={ZxLatex} />

      <p>
        gdzie <InlineMath math={"x"} /> – kwota obniżki ceny latarki
        <InlineMath math="\mathrm{L25}" /> (wyrażona w pełnych złotych), spełniająca warunki{" "}
        <InlineMath math={`1 \\le x \\le ${maxX}`} />, a{" "}
        <InlineMath math={"Z"} /> – roczny zysk ze sprzedaży latarek
        (wyrażony w złotych), liczony od momentu obniżenia ceny.
      </p>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      <p>
        Roczny zysk <InlineMath math={"Z"} /> będzie największy dla{" "}
        <InlineMath math={"x"} /> równego:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ INPUT ============

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ============ CHECK ============

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return false;
  }
  const { correctIndex } = data;
  return userAnswer.choiceIndex === correctIndex;
}

// ============ POPRAWNA ODPOWIEDŹ ============

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

// ============ ODPOWIEDŹ UCZNIA ============

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ============ WYJAŚNIENIE ============

export function renderExplanation({ data }) {
  const { A, B, C, xOpt, maxX } = data;

  // A = B * r, więc r = A/B (powinno wyjść ładnie całkowite)
  const r = A / B;
  const x1 = -r;    // pierwiastek z A + Bx = 0
  const x2 = C;     // pierwiastek z C - x = 0

  // Do alternatywnego sposobu (jak wcześniej):
  const a = -B;
  const b = B * C - A;
  const c = A * C;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      {/* DOMYŚLNY SPOSÓB – z postaci iloczynowej */}
      <p>
        <strong>Krok 1.</strong> Zauważ, że funkcja zysku ma postać iloczynową:
      </p>

      <BlockMath
        math={`Z(x) = ( ${A} + ${B}x )( ${C} - x ).`}
      />

      <p>
        Wartość zysku <InlineMath math={"Z(x)"} /> jest równa zero, gdy
        któryś z nawiasów jest równy zero, czyli gdy:
      </p>

      <BlockMath math={`${A} + ${B}x = 0`} />
      <p>lub</p>
      <BlockMath math={`${C} - x = 0.`} />

      <p>
        Rozwiązujemy oba równania:
      </p>

      <BlockMath
        math={`x_1 = -\\frac{${A}}{${B}} = -${r},\\quad x_2 = ${C}.`}
      />

      <p>
        Otrzymaliśmy więc pierwiastki trójmianu kwadratowego:{" "}
        <InlineMath math={`x_1 = -${r}`} /> oraz{" "}
        <InlineMath math={`x_2 = ${C}`} />.
      </p>

      <p>
        <strong>Krok 2.</strong> Oś symetrii paraboli jest dana wzorem
        (średnia arytmetyczna pierwiastków):
      </p>

      <BlockMath
        math={`x_0 = \\frac{x_1 + x_2}{2} = \\frac{ -${r} + ${C} }{2} = ${xOpt}.`}
      />

      <p>
        <strong>Krok 3.</strong> Ponieważ ramiona paraboli są skierowane w dół
        (współczynnik przy <InlineMath math={"x^2"} /> jest ujemny), funkcja{" "}
        <InlineMath math={"Z(x)"} /> przyjmuje wartość maksymalną właśnie dla{" "}
        <InlineMath math={"x = x_0"} />.
      </p>
      <p>
        Wartość <InlineMath math={String(xOpt)} /> jest całkowita i spełnia{" "}
        <InlineMath math={`1\\le ${xOpt}\\le ${maxX}`} />, więc jest dopuszczalną kwotą obniżki.
      </p>

      <p>
        Zatem roczny zysk będzie największy dla{" "}
        <InlineMath math={`x = ${xOpt}`} />.
      </p>

      {/* ALTERNATYWNY SPOSÓB – schowany */}
      <details style={{ marginTop: "1rem" }}>
        <summary style={{ cursor: "pointer" }}>
          Inny sposób: z postaci ogólnej i wzoru{" "}
          <InlineMath math={"x_0 = -\\dfrac{b}{2a}"} />
        </summary>

        <div style={{ marginTop: "0.5rem" }}>
          <p>
            Możemy również skorzystać z przepisania funkcji do postaci ogólnej
            i wzoru na współrzędną wierzchołka paraboli.
          </p>

          <p>
            Rozwijamy iloczyn:
          </p>

          <BlockMath
            math={`Z(x) = ( ${A} + ${B}x )( ${C} - x ) = -${B}x^2 + (${B}\\cdot ${C} - ${A})x + ${A}\\cdot ${C}.`}
          />

          <p>
            Odczytujemy współczynniki:
          </p>

          <BlockMath
            math={`a = -${B},\\quad b = ${B}\\cdot ${C} - ${A},\\quad c = ${A}\\cdot ${C}.`}
          />

          <p>
            Współrzędna wierzchołka paraboli jest dana wzorem{" "}
            <InlineMath math={"x_0 = -\\dfrac{b}{2a}"} />:
          </p>

          <BlockMath
            math={`x_0 = -\\frac{${B}\\cdot ${C} - ${A}}{2\\cdot(-${B})}
            = \\frac{${B}\\cdot ${C} - ${A}}{2\\cdot ${B}} = ${xOpt}.`}
          />

          <p>
            Ponieważ <InlineMath math={"a < 0"} />, funkcja{" "}
            <InlineMath math={"Z(x)"} /> osiąga wartość maksymalną dla{" "}
            <InlineMath math={"x = x_0"} />, czyli{" "}
            <InlineMath math={`x = ${xOpt}`} /> – to samo, co wcześniej.
          </p>
        </div>
      </details>
    </div>
  );
}

